import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { knowledgeAPI } from "../services/api";
import Rating from "../components/knowledge/Rating";

const KnowledgeDetailPage = () => {
  const { id } = useParams();
  const [knowledge, setKnowledge] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKnowledge = async () => {
      setLoading(true);
      try {
        const response = await knowledgeAPI.getById(id);
        setKnowledge(response.data.data);
        setRelated(response.data.related || []);

        // Update view count
        await knowledgeAPI.updateView(id);
      } catch (error) {
        console.error("Failed to fetch knowledge:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledge();
  }, [id]);

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";

    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return url;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-circle text-4xl text-red-500"></i>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            ไม่พบข้อมูล
          </h2>
          <p className="text-gray-600 mb-6">ไม่พบองค์ความรู้ที่คุณต้องการ</p>
          <Link
            to="/knowledge"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            <i className="fas fa-arrow-left"></i>
            กลับไปหน้าคลังปัญญา
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {knowledge?.title ? `${knowledge.title} | คลังปัญญา` : "คลังปัญญา"}
        </title>
        {knowledge?.content && (
          <meta
            name="description"
            content={knowledge.content.substring(0, 160)}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-8 flex-wrap">
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              หน้าแรก
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <Link
              to="/knowledge"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              คลังปัญญา
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <span className="text-gray-800 font-medium line-clamp-1">
              {knowledge.title}
            </span>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content - 3/4 width */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header */}
                <div className="p-8 border-b">
                  <h1 className="text-3xl lg:text-4xl font-bold text-primary-500 mb-4">
                    {knowledge.title}
                  </h1>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <i className="fas fa-user text-primary-500"></i>
                      {knowledge.author || "ไม่ระบุ"}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="fas fa-calendar text-primary-500"></i>
                      {formatDate(knowledge.publish_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="fas fa-folder text-primary-500"></i>
                      <Link
                        to={`/categories/${knowledge.catkm_id}`}
                        className="text-primary-500 hover:underline"
                      >
                        {knowledge.catkm_name || "ไม่ระบุหมวด"}
                      </Link>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="fas fa-eye text-primary-500"></i>
                      {knowledge.views || 0} ครั้ง
                    </span>
                  </div>

                  {/* Rating */}
                  <div className="mt-4 pt-4 border-t">
                    <Rating
                      knowledgeId={knowledge.knowledge_id}
                      initialRating={knowledge.rating}
                      ratingCount={knowledge.rating_count}
                    />
                  </div>
                </div>

                {/* Video Section */}
                {knowledge.main_vdo_url && (
                  <div className="p-8 border-b">
                    <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                      <i className="fas fa-video text-secondary-500 mr-2"></i>
                      วิดีโอประกอบ
                    </h3>
                    <div className="relative pb-[56.25%] h-0 rounded-lg overflow-hidden shadow-lg">
                      <iframe
                        src={getYoutubeEmbedUrl(knowledge.main_vdo_url)}
                        title={knowledge.title}
                        className="absolute top-0 left-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                    <i className="fas fa-align-left text-secondary-500 mr-2"></i>
                    รายละเอียด
                  </h3>
                  <div
                    className="prose max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: knowledge.content }}
                  />
                </div>

                {/* Share Section */}
                <div className="p-8 bg-gray-50 border-t">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    แชร์องค์ความรู้นี้
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert("คัดลอกลิงก์เรียบร้อย");
                      }}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="fas fa-link"></i>
                    </button>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#1877f2] text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a
                      href={`https://line.me/R/msg/text/?${knowledge.title}%20${window.location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#00c300] text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                    >
                      <i className="fab fa-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - 1/4 width */}
            {related.length > 0 && (
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                  <h3 className="text-lg font-semibold text-primary-500 mb-4 pb-2 border-b flex items-center">
                    <i className="fas fa-list text-secondary-500 mr-2"></i>
                    องค์ความรู้อื่นๆ
                  </h3>
                  <div className="space-y-4">
                    {related.map((item) => (
                      <Link
                        key={item.knowledge_id}
                        to={`/knowledge/${item.knowledge_id}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={
                              item.main_img_url ||
                              "/assets/knowledge/no-img.png"
                            }
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/knowledge/no-img.png";
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {item.title}
                          </h4>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Back to Knowledge */}
                  <div className="mt-6 pt-4 border-t">
                    <Link
                      to="/knowledge"
                      className="flex items-center justify-center gap-2 text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      <i className="fas fa-arrow-left"></i>
                      <span>กลับไปหน้าคลังปัญญา</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default KnowledgeDetailPage;
